import type { FeatureKey } from '../services/subscriptionGuard'

export const LIFETIME_PASS_IDS = ['SOLO_LIFETIME', 'CREATOR_LIFETIME', 'POWERHOUSE_LIFETIME'] as const
export type LifetimePassId = typeof LIFETIME_PASS_IDS[number]
export type LifetimeEntitlement = { id: string; workspaceId: string; type: 'LIFETIME_PASS'; passId: LifetimePassId; status: 'ACTIVE' | 'REVOKED'; purchasedAt?: unknown }

export type LifetimeCapKey =
  | 'maxGoalsPerBoard'
  | 'maxTasksPerBoard'
  | 'maxTasksPerGoal'
  | 'maxSubtasksPerBoard'
  | 'maxSubtasksPerTask'
  | 'maxChecklistsPerBoard'
  | 'maxChecklistsPerContainer'

export const LIFETIME_PASS_WORKSPACE_CAPS: Record<LifetimePassId, Record<LifetimeCapKey, number>> = {
  SOLO_LIFETIME: {
    maxGoalsPerBoard: 10,
    maxTasksPerBoard: 500,
    maxTasksPerGoal: 50,
    maxSubtasksPerBoard: 12500,
    maxSubtasksPerTask: 25,
    maxChecklistsPerBoard: 10200,
    maxChecklistsPerContainer: 20,
  },
  CREATOR_LIFETIME: {
    maxGoalsPerBoard: 100,
    maxTasksPerBoard: 50000,
    maxTasksPerGoal: 500,
    maxSubtasksPerBoard: 1250000,
    maxSubtasksPerTask: 25,
    maxChecklistsPerBoard: 1002000,
    maxChecklistsPerContainer: 20,
  },
  POWERHOUSE_LIFETIME: {
    maxGoalsPerBoard: -1,
    maxTasksPerBoard: -1,
    maxTasksPerGoal: -1,
    maxSubtasksPerBoard: -1,
    maxSubtasksPerTask: -1,
    maxChecklistsPerBoard: -1,
    maxChecklistsPerContainer: -1,
  },
}

export const LIFETIME_PASSES: Record<LifetimePassId, {
  name: string
  priceInr: number
  description: string
  limits: Partial<Record<FeatureKey, number>>
  workspaceCaps: Record<LifetimeCapKey, number>
}> = {
  SOLO_LIFETIME: {
    name: 'Solo Lifetime',
    priceInr: 999,
    description: 'Focused planning for your owned workspace pair with 10 GiB storage.',
    limits: { workspaces: 1, storageGB: 10, activeIntegrations: 0, meetingsPerMonth: 5, syncSpeedSeconds: 15, webhookCallsPerMonth: 0, aiChatHistories: -1, aiContextAttachments: 50, maxSavedCharts: 50 },
    workspaceCaps: LIFETIME_PASS_WORKSPACE_CAPS.SOLO_LIFETIME,
  },
  CREATOR_LIFETIME: {
    name: 'Creator Lifetime',
    priceInr: 1499,
    description: 'Creator-scale limits, multiplayer, integrations, and 250 GiB storage for your owned workspace pair.',
    limits: { workspaces: 1, storageGB: 250, activeIntegrations: 5, meetingsPerMonth: 50, syncSpeedSeconds: 5, webhookCallsPerMonth: 500, aiChatHistories: -1, aiContextAttachments: 50, maxSavedCharts: 50 },
    workspaceCaps: LIFETIME_PASS_WORKSPACE_CAPS.CREATOR_LIFETIME,
  },
  POWERHOUSE_LIFETIME: {
    name: 'Powerhouse Lifetime',
    priceInr: 1999,
    description: 'White-label access with unlimited non-workspace feature quotas.',
    limits: { workspaces: 1, storageGB: -1, chatHistoryDays: -1, chatMessageLimit: -1, activeIntegrations: -1, meetingsPerMonth: -1, webhookCallsPerMonth: -1, auditLogRetentionDays: -1, syncSpeedSeconds: 0, aiChatHistories: -1, aiContextAttachments: 50, maxSavedCharts: -1 },
    workspaceCaps: LIFETIME_PASS_WORKSPACE_CAPS.POWERHOUSE_LIFETIME,
  },
}

export const effectiveEntitlementLimit = (values: number[]) => values.includes(-1) ? -1 : Math.max(...values.filter(Number.isFinite), 0)
